import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { authApi } from '../api'
import type { User, AccountConfig } from '../types'
import { useAccounts } from './AccountContext'

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (username: string, password: string) => Promise<void>
  loginWithAccount: (config: AccountConfig) => Promise<void>
  logout: () => void
  logoutAccount: (accountId: string) => void
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const { currentAccount, addAccount, removeAccount } = useAccounts()

  useEffect(() => {
    // Load user from current account or access_token
    const loadUser = async () => {
      // Check if we have a token (either from account or direct localStorage)
      const hasToken = currentAccount?.token || localStorage.getItem('access_token')

      if (hasToken) {
        try {
          const currentUser = await authApi.getCurrentUser()
          setUser(currentUser)
        } catch (error) {
          console.error('Failed to load user:', error)
          // Token might be expired, remove the account if it exists
          if (currentAccount?.id) {
            removeAccount(currentAccount.id)
          } else {
            // Clear the standalone token
            localStorage.removeItem('access_token')
          }
        }
      }
      setLoading(false)
    }

    loadUser()
  }, [currentAccount])

  const login = async (username: string, password: string) => {
    const response = await authApi.login({ username, password })
    localStorage.setItem('access_token', response.access_token)
    const currentUser = await authApi.getCurrentUser()
    setUser(currentUser)
  }

  const loginWithAccount = async (config: AccountConfig) => {
    const response = await authApi.login({ username: config.username, password: config.password })

    // Store token in localStorage so getCurrentUser can use it
    localStorage.setItem('access_token', response.access_token)

    const currentUser = await authApi.getCurrentUser()

    // Add the account to the accounts list
    addAccount(config, response.access_token, currentUser)
    setUser(currentUser)
  }

  const logout = () => {
    if (currentAccount) {
      removeAccount(currentAccount.id)
    }
    localStorage.removeItem('access_token')
    setUser(null)
  }

  const logoutAccount = (accountId: string) => {
    removeAccount(accountId)
    if (currentAccount?.id === accountId) {
      setUser(null)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginWithAccount,
        logout,
        logoutAccount,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
